import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Bug,
  FileText,
  Lightbulb,
  type LucideIcon,
  MessageCircle,
  MessageSquareText,
  ShieldAlert,
  X,
} from "lucide-react";
import type { JSX } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { V3_CHECKBOX, V3_INPUT } from "@/components/ui/v3/flagged";
import {
  FEEDBACK_MESSAGE_MAX,
  FEEDBACK_TYPES,
  type FeedbackDraft,
  type FeedbackSendError,
  type FeedbackType,
} from "@/lib/in-app-feedback";
import { cn } from "@/lib/utils";

const ICONS: Record<FeedbackType, LucideIcon> = {
  bug: Bug,
  idea: Lightbulb,
  note_quality: FileText,
  other: MessageCircle,
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // The visit it was opened from; offers "include this visit" (on by
  // default).
  visitPublicId?: string | null;
  initialType?: FeedbackType;
  // Resolves when the report was accepted; rejects with the reason shown.
  onSubmit: (draft: FeedbackDraft) => Promise<void>;
  // Maps a rejection to the message shown under the form.
  describeError: (error: unknown) => FeedbackSendError;
}

// "Send feedback": a bottom sheet on phones, a centred dialog from 640px.
// Type, a required message, the privacy notice and, from a visit, whether to
// include the visit's id. Nothing else is collected (no screenshots).
export const FeedbackDialog = ({
  open,
  onOpenChange,
  visitPublicId,
  initialType = "bug",
  onSubmit,
  describeError,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  // v3.2 app (VITE_APP_REDESIGN_ENABLED): v3 tokens; classic otherwise.
  const r = AppConfig.enableAppRedesign === true;
  const pick = (classic: string, v3: string): string => (r ? v3 : classic);
  const [type, setType] = useState<FeedbackType>(initialType);
  const [message, setMessage] = useState("");
  const [includeVisit, setIncludeVisit] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<FeedbackSendError | null>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const ids = {
    type: useId(),
    message: useId(),
    count: useId(),
    privacy: useId(),
    visit: useId(),
    visitHint: useId(),
  };

  // A fresh form each time it opens.
  useEffect(() => {
    if (!open) return;
    setType(initialType);
    setMessage("");
    setIncludeVisit(true);
    setSending(false);
    setError(null);
  }, [open, initialType]);

  const trimmed = message.trim();
  const canSend = trimmed.length > 0 && !sending;

  const submit = async (): Promise<void> => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      await onSubmit({
        type,
        message: trimmed,
        visitPublicId: visitPublicId && includeVisit ? visitPublicId : null,
      });
    } catch (reason) {
      setError(describeError(reason));
      setSending(false);
    }
  };

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!sending) onOpenChange(next);
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            pick(
              "bg-black/40 backdrop-blur-[2px] dark:bg-black/60",
              "bg-black/30 dark:bg-black/50",
            ),
          )}
        />
        <DialogPrimitive.Content
          aria-describedby={ids.privacy}
          className={cn(
            "fixed z-50 flex flex-col overflow-hidden outline-hidden duration-300",
            pick(
              "bg-background text-foreground shadow-2xl",
              "bg-v3-panel font-v3 text-v3-text shadow-v3-pop",
            ),
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            // Phones: bottom sheet.
            "inset-x-0 bottom-0 max-h-[92dvh] data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            pick(
              "rounded-t-[28px] border-t border-tinted-grey-1",
              "rounded-t-[22px]",
            ),
            // From 640px: centred dialog.
            "sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[min(720px,calc(100dvh-48px))] sm:w-[min(560px,calc(100vw-48px))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95",
            pick("sm:rounded-[28px] sm:border", "sm:rounded-[18px]"),
            "motion-reduce:animate-none",
          )}
          data-testid="feedback-dialog"
          onOpenAutoFocus={(event) => {
            // Straight to the message on desktop; phones keep the keyboard
            // closed until the field is tapped.
            event.preventDefault();
            if (window.matchMedia?.("(min-width: 640px)").matches)
              messageRef.current?.focus({ preventScroll: true });
          }}
        >
          <form
            className="flex min-h-0 flex-1 flex-col"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <div
              aria-hidden
              className={cn(
                "mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full sm:hidden",
                pick("bg-foreground/15", "bg-v3-border-strong"),
              )}
            />
            <div
              className={cn(
                "relative shrink-0 px-5 pb-3 pt-4",
                pick("sm:px-7 sm:pb-4 sm:pt-7", "sm:px-6 sm:pb-4 sm:pt-6"),
              )}
            >
              {!r && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 -top-24 h-48 bg-[radial-gradient(60%_100%_at_30%_0%,hsl(var(--primary)/0.12),transparent)] dark:bg-[radial-gradient(60%_100%_at_30%_0%,hsl(var(--primary)/0.2),transparent)]"
                />
              )}
              <div className="relative flex items-center gap-3.5">
                <span
                  aria-hidden
                  className={cn(
                    "flex shrink-0 items-center justify-center",
                    pick(
                      "size-11 rounded-2xl bg-primary text-primary-foreground shadow-[0_6px_16px_-6px_hsl(var(--primary)/0.7)]",
                      "size-10 rounded-[12px] bg-v3-surface-2 text-v3-text shadow-v3-hairline",
                    ),
                  )}
                >
                  <MessageSquareText
                    className={pick("size-[22px]", "size-5")}
                    strokeWidth={2}
                  />
                </span>
                <div className="min-w-0 pr-10">
                  <DialogPrimitive.Title
                    className={pick(
                      "text-[22px] font-extrabold leading-tight tracking-tight",
                      "text-[17px] font-semibold leading-6 text-v3-text",
                    )}
                  >
                    {t("feedback.inApp.title")}
                  </DialogPrimitive.Title>
                  <p
                    className={pick(
                      "mt-0.5 text-pretty text-sm text-foreground/70",
                      "mt-0.5 text-pretty text-[13.5px] leading-snug text-v3-text-2",
                    )}
                  >
                    {t("feedback.inApp.subtitle")}
                  </p>
                </div>
              </div>
              <DialogPrimitive.Close
                aria-label={t("common.close")}
                className={cn(
                  "absolute right-2 top-2 flex size-11 items-center justify-center rounded-full transition-colors disabled:opacity-50",
                  pick(
                    "text-foreground/60 hover:bg-foreground/5 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:right-4 sm:top-4",
                    "text-v3-text-2 hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-2 focus-visible:outline-v3-brand sm:right-3 sm:top-3 sm:size-9",
                  ),
                )}
                disabled={sending}
              >
                <X aria-hidden className="size-5" />
              </DialogPrimitive.Close>
            </div>

            <div
              className={cn(
                "min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-5 pb-4",
                pick("sm:px-7", "sm:px-6"),
              )}
            >
              <fieldset>
                <legend
                  className={pick(
                    "mb-2 text-sm font-semibold text-foreground",
                    "mb-2 text-[13px] font-medium text-v3-text-2",
                  )}
                >
                  {t("feedback.inApp.typeLabel")}
                </legend>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {FEEDBACK_TYPES.map((value) => {
                    const Icon = ICONS[value];
                    return (
                      <label
                        key={value}
                        className={cn(
                          "flex min-h-12 cursor-pointer items-center gap-2 px-3 text-sm font-semibold transition-colors sm:min-h-[76px] sm:flex-col sm:items-start sm:justify-center sm:gap-1.5 sm:py-2.5",
                          pick(
                            "rounded-xl border border-tinted-grey-2 bg-grey-white text-foreground/80 hover:border-primary/50 hover:text-foreground has-checked:border-primary has-checked:bg-primary/10 has-checked:text-primary dark:has-checked:bg-primary/20 has-focus-visible:ring-2 has-focus-visible:ring-ring has-focus-visible:ring-offset-2 has-focus-visible:ring-offset-background",
                            "rounded-[12px] bg-v3-surface-2 text-[13.5px] text-v3-text-2 shadow-v3-hairline hover:bg-v3-surface-3 hover:text-v3-text has-checked:bg-v3-panel has-checked:text-v3-text has-checked:shadow-[inset_0_0_0_2px_var(--v3-ink)] has-focus-visible:ring-2 has-focus-visible:ring-v3-brand-ring",
                          ),
                          "has-disabled:cursor-not-allowed has-disabled:opacity-60",
                        )}
                        title={t(`feedback.inApp.typeHints.${value}`)}
                      >
                        <input
                          checked={value === type}
                          className="sr-only"
                          data-testid={`feedback-type-${value}`}
                          disabled={sending}
                          name={ids.type}
                          type="radio"
                          value={value}
                          onChange={() => {
                            setType(value);
                          }}
                        />
                        <Icon aria-hidden className="size-[18px] shrink-0" />
                        <span className="leading-tight">
                          {t(`feedback.inApp.types.${value}`)}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p
                  className={pick(
                    "mt-2 text-xs text-foreground/60",
                    "mt-2 text-[12.5px] text-v3-text-3",
                  )}
                  aria-live="polite"
                >
                  {t(`feedback.inApp.typeHints.${type}`)}
                </p>
              </fieldset>

              <div>
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <label
                    className={pick(
                      "text-sm font-semibold text-foreground",
                      "text-[13px] font-medium text-v3-text-2",
                    )}
                    htmlFor={ids.message}
                  >
                    {t("feedback.inApp.messageLabel")}
                  </label>
                  <span
                    className={cn(
                      pick(
                        "text-xs tabular-nums text-foreground/50",
                        "text-[12px] tabular-nums text-v3-text-3",
                      ),
                      message.length > FEEDBACK_MESSAGE_MAX * 0.9 &&
                        pick("text-foreground/80", "text-v3-text"),
                    )}
                    id={ids.count}
                  >
                    {t("feedback.inApp.characters", {
                      count: message.length,
                      max: FEEDBACK_MESSAGE_MAX,
                    })}
                  </span>
                </div>
                <textarea
                  ref={messageRef}
                  aria-describedby={`${ids.privacy} ${ids.count}`}
                  aria-required="true"
                  className={cn(
                    "block min-h-[140px] w-full resize-y px-3.5 py-3 text-base leading-relaxed disabled:opacity-60 sm:min-h-[160px] sm:text-[15px]",
                    pick(
                      "rounded-xl border border-tinted-grey-2 bg-tinted-grey-1 text-foreground placeholder:text-foreground/45 focus-visible:border-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30",
                      `${V3_INPUT} rounded-[12px] focus-visible:outline-hidden`,
                    ),
                  )}
                  data-testid="feedback-message"
                  disabled={sending}
                  id={ids.message}
                  maxLength={FEEDBACK_MESSAGE_MAX}
                  placeholder={t(`feedback.inApp.placeholders.${type}`)}
                  value={message}
                  onChange={(event) => {
                    setMessage(event.target.value);
                    if (error) setError(null);
                  }}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" &&
                      (event.metaKey || event.ctrlKey)
                    ) {
                      event.preventDefault();
                      void submit();
                    }
                  }}
                />
              </div>

              <p
                className={pick(
                  "flex gap-2.5 rounded-xl border border-warning/40 bg-warning/10 px-3.5 py-3 text-sm leading-snug text-foreground dark:bg-warning/15",
                  "flex gap-2.5 rounded-[12px] bg-v3-warn-tint px-3.5 py-3 text-[13.5px] leading-snug text-v3-text",
                )}
                data-testid="feedback-privacy"
                id={ids.privacy}
              >
                <ShieldAlert
                  aria-hidden
                  className={cn(
                    "mt-px size-[18px] shrink-0",
                    pick("text-warning", "text-v3-warn-text"),
                  )}
                />
                {t("feedback.inApp.privacy")}
              </p>

              {visitPublicId && (
                <div
                  className={pick(
                    "flex gap-3 rounded-xl border border-tinted-grey-2 px-3.5 py-3",
                    "flex gap-3 rounded-[12px] px-3.5 py-3 shadow-v3-hairline",
                  )}
                >
                  <Checkbox
                    aria-describedby={ids.visitHint}
                    checked={includeVisit}
                    className={cn("mt-0.5 size-5 rounded-md", r && V3_CHECKBOX)}
                    data-testid="feedback-include-visit"
                    disabled={sending}
                    id={ids.visit}
                    onCheckedChange={(checked) => {
                      setIncludeVisit(checked === true);
                    }}
                  />
                  <div className="min-w-0">
                    <label
                      className="cursor-pointer text-sm font-semibold leading-5"
                      htmlFor={ids.visit}
                    >
                      {t("feedback.inApp.includeVisit")}
                    </label>
                    <p
                      className={pick(
                        "mt-0.5 text-xs leading-snug text-foreground/60",
                        "mt-0.5 text-[12.5px] leading-snug text-v3-text-3",
                      )}
                      id={ids.visitHint}
                    >
                      {t("feedback.inApp.includeVisitHint")}
                    </p>
                  </div>
                </div>
              )}

              {error && (
                <p
                  className={pick(
                    "rounded-xl bg-destructive/10 px-3.5 py-3 text-sm font-medium text-destructive",
                    "rounded-[12px] bg-v3-rec-tint px-3.5 py-3 text-[13.5px] font-medium text-v3-rec-text",
                  )}
                  data-testid="feedback-error"
                  role="alert"
                >
                  {t(`feedback.inApp.errors.${error}`)}
                </p>
              )}
            </div>

            <div
              className={pick(
                "flex shrink-0 flex-col-reverse gap-2 border-t border-tinted-grey-1 px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3 sm:flex-row sm:justify-end sm:border-t-0 sm:px-7 sm:pb-7 sm:pt-2",
                "flex shrink-0 flex-col-reverse gap-2 border-t border-v3-border px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3 sm:flex-row sm:justify-end sm:border-t-0 sm:px-6 sm:pb-6 sm:pt-2",
              )}
            >
              <DialogPrimitive.Close asChild>
                <button
                  className={pick(
                    "flex min-h-12 items-center justify-center rounded-xl px-6 text-base font-semibold text-foreground/80 transition-colors hover:bg-foreground/5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 sm:min-h-11",
                    "flex min-h-12 items-center justify-center rounded-full px-5 text-[15px] font-semibold text-v3-text-2 transition-colors hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-2 focus-visible:outline-v3-brand disabled:opacity-50 sm:min-h-9 sm:text-[13.5px]",
                  )}
                  disabled={sending}
                  type="button"
                >
                  {t("feedback.inApp.cancel")}
                </button>
              </DialogPrimitive.Close>
              <button
                aria-disabled={!canSend}
                className={pick(
                  "flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary px-7 text-base font-bold text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-primary sm:min-h-11",
                  "flex min-h-12 items-center justify-center gap-2 rounded-full bg-v3-ink px-5 text-[15px] font-semibold text-v3-on-ink transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand aria-disabled:cursor-not-allowed aria-disabled:opacity-40 sm:min-h-9 sm:text-[13.5px]",
                )}
                data-testid="feedback-send"
                type="submit"
              >
                {sending && <LoadingSpinner />}
                {sending
                  ? t("feedback.inApp.sending")
                  : t("feedback.inApp.send")}
              </button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
